/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Segmented 버튼 클래스를 정의한다.
 *
 * @file /src/components/button/Segmented.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 9. 16.
 */
import * as parent from '@base/Component';
import { Button, Properties as ButonProperties } from '@components/button/Button';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { Language } from '@utils/Language';
import './Segmented.css';

export interface Listeners<T> extends parent.Listeners<T> {
    change: (me: Segmented, value: string | number) => void;
}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;

    /**
     * 토글여부
     */
    toggle?: boolean;

    /**
     * 선택된 값
     */
    value?: string | number;

    /**
     * 버튼 컴포넌트
     */
    buttons?: ButonProperties<Button>[];
}

export class Segmented extends parent.Component {
    /**
     * 컴포넌트 타입
     */
    type = 'button';

    /**
     * 컴포넌트 확장타입
     */
    variant = 'segmented';

    /**
     * 토글여부
     */
    toggle: boolean | null = null;

    /**
     * 선택된 값
     */
    value: string | number | null = null;

    /**
     * 버튼 컴포넌트
     */
    buttons: ButonProperties<Button>[] | null = null;

    /**
     * 버튼 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Segmented> = null) {
        super(properties);

        this.toggle = this.properties.toggle === true;
        this.value = this.properties.value ?? null;
        this.layout = 'auto';
        this.buttons = this.properties.buttons ?? [];
    }

    /**
     * 선택값을 변경한다.
     *
     * @param value - 변경할 값
     * @param is_init - 초기값여부
     */
    setValue(value: string | number, is_init: boolean = false): void {
        for (const button of this.$getContent().getChildren()) {
            button.toggleClass('pressed', value === button.getAttr('value'));
        }

        if (this.value !== value) {
            this.value = value;

            if (is_init !== true) {
                this.fireEvent('change', [this, value]);
            }
        }
    }

    /**
     * 선택된 값을 가져온다.
     *
     * @returns 값
     */
    getValue(): string | number {
        return this.value;
    }

    /**
     * 컴포넌트의 비활성화여부를 설정한다.
     *
     * @param disabled - 비활성화여부
     * @returns 컴포넌트
     */
    setDisabled(disabled: boolean): this {
        for (const button of this.$getContent().getChildren()) {
            button.toggleAttr('disabled', disabled);
        }

        return super.setDisabled(disabled);
    }

    /**
     * 컴포넌트 컨테이너를 렌더링한다.
     */
    async renderContainer(): Promise<void> {
        let pressed: Dom = null;
        for (let item of this.buttons) {
            let $button = Html.create('button');
            $button.setAttr('type', 'button');
            $button.setAttr('value', String(item.value));

            if (item.icon) {
                let $icon = Html.icon(item.icon);
                $button.append($icon);
            }

            if (item.text) {
                let $text = Html.create('span');
                $text.html(await Language.resolveText(item.text));
                $button.append($text);
            }

            if (item.pressed === true) {
                pressed = $button;
            }

            if (!item.handler) {
                $button.on('click', () => {
                    this.setValue(item.value);
                });
            }

            this.$getContent().append($button);
        }

        this.$getContainer().append(this.$getContent());

        if (pressed !== null) {
            this.setValue(pressed.getAttr('value'));
        }
    }

    /**
     * 컴포넌트 내부 콘텐츠를 렌더링한다.
     * 내부 콘텐츠 렌더링이 복잡할 경우, 하위 클래스에서 재정의하여 사용한다.
     */
    async renderState(): Promise<void> {
        if (this.value !== null) {
            this.setValue(this.value);
        }
    }

    /**
     * 컴포넌트를 제거한다.
     *
     * @param removeDom - 컴포넌트 DOM 을 제거할지 여부 (기본값 : true)
     */
    async remove(removeDom: boolean = true): Promise<void> {
        super.remove(removeDom);
    }
}
